import { inject, Injectable } from "@angular/core";
import { isPropertyDefined } from "@recursyve/nice-ts-utils";
import { NiceAsyncTypeaheadProvider, NiceAsyncTypeaheadSearchResult } from "@recursyve/nice-ui-kit.v2";
import { lastValueFrom, Observable } from "rxjs";
import { map } from "rxjs/operators";

import { FindOptionsDto } from "../../common/dto/find-options.dto";
import { FindOrganisationsDto } from "../dto/find-organisations.dto";
import { OrganisationsStatus } from "../enums/organisations-status.enum";
import { Organisations } from "../models/organisations.model";
import { OrganisationsApi } from "./organisations.api";

export interface OrganisationTypeaheadOptions extends FindOptionsDto {
    status?: OrganisationsStatus;
}

@Injectable()
export class OrganisationTypeaheadApi extends NiceAsyncTypeaheadProvider<Organisations> {
    private readonly api = inject(OrganisationsApi);

    public readonly resource = "organisations";

    private readonly pageSize = 20;

    public override async getById(id: number): Promise<Organisations> {
        return lastValueFrom(this.api.getById(id));
    }

    public override search(
        searchQuery: string,
        page: number,
        options?: OrganisationTypeaheadOptions
    ): Observable<NiceAsyncTypeaheadSearchResult<Organisations>> {
        const dto: FindOrganisationsDto = {
            searchQuery,
            page,
            pageSize: this.pageSize
        };

        if (isPropertyDefined(options, "status")) {
            dto.status = options.status;
        }

        if (isPropertyDefined(options, "ignoreIds")) {
            dto.ignoreIds = options.ignoreIds.map((x) => x);
        }

        return this.api.search(dto).pipe(
            map((items) => ({
                items,
                nextPage: items.length === this.pageSize ? page + 1 : null
            }))
        );
    }

    public override format(item: Organisations): string {
        if (typeof item.name === "string") {
            return item.name;
        }

        return "";
    }
}
